import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { FilesService } from '../../../core/services/files.service';
import { FleetService } from '../../../core/services/fleet.service';
import { UserService } from '../../../core/services/user.service';
import { AppUtils } from '../../../core/config/app.utils';
import { AccidentRecordService } from '../../../core/services/accident-record.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable } from 'rxjs';
import { IDeactivateComponent } from '../../../core/guards/deactivate.gaurd';

const ADD_EDIT_ACCIDENT_ATTACHMENT = 'ADD_EDIT_ACCIDENT_ATTACHMENT';
const ADD_EDIT_ACCIDENT_DAMAGE_ATTACHMENT =
  'ADD_EDIT_ACCIDENT_DAMAGE_ATTACHMENT';

@Component({
  selector: 'app-add-edit-accident',
  templateUrl: './add-edit-accident.component.html',
  styleUrls: ['./add-edit-accident.component.scss'],
})
export class AddEditAccidentComponent implements OnInit, IDeactivateComponent {
  @ViewChild('image', { static: false }) image!: ElementRef;
  breadcrumbs: any[] = [];
  accidentId: any;
  accidentData: any;
  fleetId: any;
  fleetData: any;
  users: any[] = [];
  department: any;

  accidentForm: FormGroup | any;
  formErrors: any;

  attachmentUploading = false;
  fleetBookingsLoading = false;

  // Accident form other fields
  attachmentFiles: any[] = []; // should be able to delete
  notified = false;
  detected = false;
  notifiedOrDetectedError = false;
  needToNotifyTheInsurance = false;
  policeReportObtained = false;
  policeReportObtainedErrors = false;
  insuranceNotified: any;
  insuranceCompanyNameAlias: any;

  markers: {
    x: number;
    y: number;
    number: number;
    origin: string;
    damageType: string;
    description: string;
    files: any;
    status?: any;
    comment?: any;
  }[] = [];
  markerNumber = 1;
  isVisible = false;
  isVisibleCom = false;
  tempMouseEvent: any;
  damageImgUploading = false;
  damageType = '';
  damageDesc = '';
  damageFiles: any[] = [];
  modalStatus = 'add';
  editableNumber: any;
  editableDamage: any;
  tempNumber: any;
  bookingOptions: any[] = [];
  selectedBooking: any;
  submit = false;
  prevPage = '';
  queryParams: any;

  formatterDecimal = (value: any): string => value?.toFixed(2);

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): string | undefined {
    const confirmationMessage =
      'You have unsaved changes. Do you really want to leave?';
    $event.returnValue = confirmationMessage; // Standard for most browsers
    return confirmationMessage; // For some older browsers
  }

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private filesService: FilesService,
    private fleetService: FleetService,
    private userService: UserService,
    private accidentService: AccidentRecordService,
    private router: Router,
    private message: NzMessageService,
  ) {
    this.formErrors = {
      fleet: {},
      accidentType: {},
      insuranceDetails: {
        thirdPartyVehicleRegisterNo: {},
      },
      bookingDetails: {
        booking: {},
      },
      driverDetails: {
        systemDriver: {},
        driverName: {},
        driverContactNo: {},
        driverAddress: {},
        thirdPartyDriverContactNo: {},
      },
      accidentDetails: {
        accidentDateAndTime: {},
        placeOfTheAccident: {},
        notifiedOrDetectedUser: {},
        remarks: {},
      },
      paymentDetails: {
        depositAmount: {},
        damageCharge: {},
        lossOfRentalCharge: {},
        totalCharge: {},
      },
    };
  }

  ngOnInit() {
    this.initAccidentForm();
    this.userService.loggedInUserSelectedDepartment.subscribe((res) => {
      this.department = res as string;

      if (this.department) {
        this.userService
          .getAllActiveUsersByDepartment({ department: this.department })
          .then((res) => {
            this.users = res as any;
          });
      } else {
        this.userService
          .getAllActiveUsersByDepartment({ type: 'ALL_APPLICABLE' })
          .then((res) => {
            this.users = res as any;
          });
      }
    });
    this.fleetId = this.route.snapshot.paramMap.get('fleetId');
    this.fleetService.getOneFleetById(this.fleetId).then((res) => {
      this.fleetData = res as any;
      this.setBreadcrumbs(res);
      this.insuranceCompanyNameAlias =
        this.fleetData?.otherDetails?.insuranceName;
    });
    this.route.queryParams.subscribe((params) => {
      this.prevPage = params['booking'];
      this.queryParams = params;
      if (this.prevPage) {
        this.getDetailsFromQueryParams('BOOKING', this.prevPage);
        this.accidentForm.patchValue({
          accidentType: 'BOOKING',
          bookingDetails: {
            booking: this.prevPage,
          },
        });
        this.accidentForm.get('bookingDetails').get('booking').disable();
        this.accidentForm.get('accidentType').disable();
      }
    });

    this.accidentForm.patchValue({
      fleet: this.fleetId,
    });

    this.filesService.fileUploadingSubject.subscribe((res: any) => {
      if (res.type === ADD_EDIT_ACCIDENT_DAMAGE_ATTACHMENT) {
        this.damageImgUploading = res.loading;
      }
      if (res.type === ADD_EDIT_ACCIDENT_ATTACHMENT) {
        this.attachmentUploading = res.loading;
      }
    });
    // console.log(this.prevPage, 'prevPage');
    // console.log(this.queryParams, 'params');
  }

  initAccidentForm(): void {
    this.accidentForm = this.fb.group({
      fleet: [null, [Validators.required]],
      description: [null, []],
      accidentType: [null, [Validators.required]],
      reason: [null, []],
      bookingDetails: this.fb.group({
        booking: [null, []],
        type: [null, []],
      }),
      insuranceDetails: this.fb.group({
        thirdPartyVehicleRegisterNo: [null, []],
        thirdPartyDriverName: [null, []],
        thirdPartyDriverContactNo: [null, []],
        thirdPartyInsuranceNo: [null, []],
        thirdPartyInsuranceExpiryDate: [null, []],
      }),
      driverDetails: this.fb.group({
        systemDriver: [null, []],
        driverName: [null, [Validators.required]],
        driverContactNo: [null, [Validators.required]],
        driverAddress: [null, [Validators.required]],
      }),
      accidentDetails: this.fb.group({
        accidentDateAndTime: [null, [Validators.required]],
        placeOfTheAccident: [null, [Validators.required]],
        notifiedOrDetectedUser: [null, [Validators.required]],
        remarks: [null, []],
      }),
      paymentDetails: this.fb.group({
        depositAmount: [0, []],
        damageCharge: [0, []],
        lossOfRentalCharge: [0, []],
        totalCharge: [{ value: 0, disabled: true }, []],
      }),
    });

    this.accidentForm.valueChanges.subscribe(() => {
      // this.formErrors = AppUtils.getFormErrors(this.accidentForm, this.formErrors);
      this.formErrors.insuranceDetails = AppUtils.getFormErrors(
        this.accidentForm.get('insuranceDetails'),
        this.formErrors.insuranceDetails,
      );
      this.formErrors.driverDetails = AppUtils.getFormErrors(
        this.accidentForm.get('driverDetails'),
        this.formErrors.driverDetails,
      );
      this.formErrors.accidentDetails = AppUtils.getFormErrors(
        this.accidentForm.get('accidentDetails'),
        this.formErrors.accidentDetails,
      );
      this.formErrors.bookingDetails = AppUtils.getFormErrors(
        this.accidentForm.get('bookingDetails'),
        this.formErrors.bookingDetails,
      );
    });

    this.accidentForm
      .get('paymentDetails')
      .valueChanges.subscribe((values: any) => {
        const total =
          values.damageCharge +
          values.lossOfRentalCharge;
        this.accidentForm
          .get('paymentDetails')
          .controls.totalCharge.setValue(total, {
            onlySelf: true,
            emitEvent: false,
          });
      });
  }

  formatterLkr = (value: number): string => `LKR ${value}`;

  setBreadcrumbs(data: any): void {
    this.breadcrumbs = [
      {
        title: ' Fleets ',
        icon: 'car',
        route: '/admin/fleets',
      },
      {
        title: ` ${data?.vehicleDetails?.registrationNumber} `,
        route: `/admin/fleets/${this.fleetId}`,
      },
      {
        title: ' Accident Records ',
        route: [`/admin/fleets/${this.fleetId}`, { pageName: 'fleetAccident' }],
      },
      {
        title: ' Add ',
      },
    ];
  }

  attachmentUpload = (item: NzUploadXHRArgs) =>
    this.filesService
      .postFile(item.file, ADD_EDIT_ACCIDENT_ATTACHMENT)
      .subscribe((res) => {
        this.message.create('success', `Attachment has been uploaded!`);
        const data = res as any;
        this.attachmentFiles.push({ name: item.file.name, url: data.url });
        this.filesService.fileUploadingSubject.next({
          type: ADD_EDIT_ACCIDENT_ATTACHMENT,
          loading: false,
        });
      });

  triggerFileInput() {
    document.getElementById('fileInput')?.click(); // Trigger click event on file input
  }

  setNotifiedOrDetected(type: string): void {
    if (type === 'notified') {
      if (this.notified) {
        this.notified = false;
        this.detected = false;
        this.notifiedOrDetectedError = true;
      } else {
        this.notified = true;
        this.detected = false;
        this.notifiedOrDetectedError = false;
      }
    } else {
      if (this.detected) {
        this.notified = false;
        this.detected = false;
        this.notifiedOrDetectedError = true;
      } else {
        this.notified = false;
        this.detected = true;
        this.notifiedOrDetectedError = false;
      }
    }
  }

  setInsuranceNotified(type: string): void {
    if (type === 'ON_SITE') {
      if (this.insuranceNotified === 'ON_SITE') {
        this.insuranceNotified = null;
      } else {
        this.insuranceNotified = 'ON_SITE';
      }
    }
    if (type === 'ON_DEPARTURE') {
      if (this.insuranceNotified === 'ON_DEPARTURE') {
        this.insuranceNotified = null;
      } else {
        this.insuranceNotified = 'ON_DEPARTURE';
      }
    }
  }

  setNeedToNotifyInsuranceAndPoliceReportObtained(
    type: string,
    action: string,
  ): void {
    switch (action) {
      case 'insurance':
        this.needToNotifyTheInsurance = type === 'YES';
        break;
      case 'police':
        if (type === 'YES') {
          this.policeReportObtained = true;
          this.policeReportObtainedErrors = false;
        } else if (type === 'NO') {
          this.policeReportObtained = false;
          this.policeReportObtainedErrors = false;
        }
        break;
    }
  }

  showModal(event: MouseEvent): void {
    this.tempMouseEvent = event;
    this.isVisible = true;
  }

  handleOk(event: MouseEvent): void {
    this.placeMarker(event);
    this.isVisible = false;
  }

  handleCancel(): void {
    console.log('Button cancel clicked!');
    this.modalStatus = 'add';
    this.damageType = '';
    this.damageDesc = '';
    this.damageFiles = [];
    this.isVisible = false;
  }

  damageUpload = (item: NzUploadXHRArgs) =>
    this.filesService
      .postFile(item.file, ADD_EDIT_ACCIDENT_DAMAGE_ATTACHMENT)
      .subscribe((res) => {
        this.message.create('success', `Attachment has been uploaded!`);
        const data = res as any;
        this.damageFiles.push({ name: item.file.name, url: data.url });
        this.filesService.fileUploadingSubject.next({
          type: ADD_EDIT_ACCIDENT_DAMAGE_ATTACHMENT,
          loading: false,
        });
      });

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (files && files.length > 0) {
      const file: File = files[0];
      this.filesService
        .postFile(file, ADD_EDIT_ACCIDENT_DAMAGE_ATTACHMENT)
        .subscribe((res) => {
          this.message.create('success', `Attachment has been uploaded!`);
          const data = res as any;
          this.damageFiles.push({ name: file.name, url: data.url });
          this.filesService.fileUploadingSubject.next({
            type: ADD_EDIT_ACCIDENT_DAMAGE_ATTACHMENT,
            loading: false,
          });
        });
    }
  }

  placeMarker(event: MouseEvent) {
    const imageElement: HTMLImageElement = this.image.nativeElement;
    const rect = imageElement.getBoundingClientRect();

    const markerX = ((event.clientX - rect.left) / rect.width) * 100;
    const markerY = ((event.clientY - rect.top) / rect.height) * 100;
    this.message.create('success', `New damage has been added!`);
    this.markers.push({
      x: markerX,
      y: markerY,
      origin: '',
      number: this.markerNumber,
      damageType: this.damageType,
      description: this.damageDesc,
      files: this.damageFiles,
    });
    this.markerNumber++;
    this.damageType = '';
    this.damageDesc = '';
    this.damageFiles = [];
  }

  setContainerWidth() {
    const imageElement: HTMLImageElement = this.image.nativeElement;
    const containerElement: HTMLElement | null =
      document.querySelector('.image-container');

    if (containerElement) {
      containerElement.style.width = imageElement.naturalWidth + 'px';
    }
  }

  onSubmitAccidentData(): void {
    const formData: any = this.accidentForm.getRawValue();
    formData.accidentDetails.notified = this.notified ?? this.notified;
    formData.accidentDetails.detected = this.detected ?? this.detected;
    formData.accidentDetails.insuranceNotified = this.insuranceNotified
      ? this.insuranceNotified
      : null;
    formData.accidentDetails.needToNotifyTheInsurance =
      this.needToNotifyTheInsurance ?? this.needToNotifyTheInsurance;
    formData.accidentDetails.policeReportObtained =
      this.policeReportObtained ?? this.policeReportObtained;
    formData.accidentDetails.attachments =
      this.attachmentFiles.length > 0 ? this.attachmentFiles : null;
    const payload = {
      ...formData,
      damages: this.markers.length > 0 ? this.markers : null,
    };
    this.accidentService.createAccidentRecord(payload).subscribe(
      (res: any) => {
        this.message.create('success', `Accident record has been added!`);
        this.submit = true;
        this.resetData();
        if (this.prevPage) {
          if (this.queryParams.from === 'rent-booking-single-view') {
            this.router.navigate([`admin/booking-rent/${this.prevPage}`], {
              queryParams: {},
            });
          } else if (this.queryParams.from === 'rent-booking-table') {
            this.router.navigate(['admin/booking-rent']);
          } else {
            this.router.navigate([`admin/booking-other/${this.prevPage}`]);
          }
        } else {
          this.router.navigate([
            `/admin/fleets/${this.fleetId}`,
            { pageName: 'fleetAccident' },
          ]);
        }
      },
      (e: any) => {
        this.message.create('error', 'Error');
      },
    );
  }

  onSubmitCancel(): void {
    this.resetData();

    if (this.prevPage) {
      this.router.navigate([`admin/booking-other/${this.prevPage}`]);
    } else {
      this.router.navigate([
        `/admin/fleets/${this.fleetId}`,
        { pageName: 'fleetAccident' },
      ]);
    }
  }

  cancel(): void {}

  resetData(): void {
    this.accidentForm.reset();
    this.notified = false;
    this.detected = false;
    this.insuranceCompanyNameAlias = null;
    this.insuranceNotified = false;
    this.policeReportObtained = false;
    this.policeReportObtainedErrors = false;
    this.notifiedOrDetectedError = false;
    this.markers = [];
    this.attachmentFiles = [];
  }

  removeDamageAttachment(index: number): void {
    this.editableDamage.damageFiles.splice(index, 1);
    this.message.create('success', `Attachment has been deleted!`);
  }

  removeAttachment(index: number): void {
    this.attachmentFiles.splice(index, 1);
    this.message.create('success', `Attachment has been deleted!`);
  }

  editDamage(data: any, index: number): void {
    this.editableNumber = index;
    this.editableDamage = data;
    this.tempNumber = data.number;
    this.modalStatus = 'edit';
    this.damageType = data.damageType;
    this.damageDesc = data.description;
    this.damageFiles = data.files;
    this.isVisible = true;
  }

  updateDamage(): void {
    this.markers[this.editableNumber] = {
      x: this.editableDamage.x,
      y: this.editableDamage.y,
      origin: '',
      number: this.tempNumber,
      damageType: this.damageType,
      description: this.damageDesc,
      files: this.damageFiles,
    };
    this.damageType = '';
    this.damageDesc = '';
    this.damageFiles = [];
    this.editableNumber = null;
    this.editableDamage = null;
    this.tempNumber = null;
    this.modalStatus = 'add';
    this.isVisible = false;
    this.message.create('success', `Damage has been updated!`);
  }

  deleteDamage(index: number): void {
    this.markers.splice(index, 1);
    for (let i = 0; i < this.markers.length; i++) {
      this.markers[i].number = i + 1;
    }
    this.markerNumber--;
    this.message.create('success', `Damage has been deleted!`);
  }

  // booking stuff
  getBookingsByFleet(event: string): void {
    if (event === 'BOOKING') {
      this.fleetBookingsLoading = true;
      this.fleetService
        .getAllBookingsByFleet(this.fleetId)
        .subscribe((res: any) => {
          this.fleetBookingsLoading = false;
          this.bookingOptions = res;
        });

      this.accidentForm
        .get('bookingDetails')
        .get('booking')
        .addValidators(Validators.required);
      this.accidentForm
        .get('bookingDetails')
        .get('type')
        .addValidators(Validators.required);
      this.accidentForm
        .get('bookingDetails')
        .get('booking')
        .updateValueAndValidity({ emitEvent: false, onlySelf: true });
      this.accidentForm
        .get('bookingDetails')
        .get('type')
        .updateValueAndValidity({ emitEvent: false, onlySelf: true });
    } else {
      this.accidentForm.get('bookingDetails').get('booking').clearValidators();
      this.accidentForm.get('bookingDetails').get('type').clearValidators();
      this.accidentForm.get('bookingDetails').reset();
      this.accidentForm
        .get('bookingDetails')
        .get('booking')
        .updateValueAndValidity({ emitEvent: false, onlySelf: true });
      this.accidentForm
        .get('bookingDetails')
        .get('type')
        .updateValueAndValidity({ emitEvent: false, onlySelf: true });
      this.selectedBooking = null;
    }
  }

  async setBookingDetails(event: string): Promise<void> {
    this.selectedBooking = this.bookingOptions?.find(
      (op: any) => op._id === event,
    );
    this.accidentForm.patchValue({
      driverDetails: {
        driverName: this.selectedBooking?.driverInfo?.name,
        driverContactNo: this.selectedBooking?.driverInfo?.contactNo,
      },
    });
    this.accidentForm.patchValue({
      paymentDetails: {
        depositAmount: this.selectedBooking?.depositAmount
          ? this.selectedBooking?.depositAmount
          : 0,
      },
    });
    this.accidentForm.patchValue({
      bookingDetails: {
        type: this.selectedBooking?.type,
      },
    });
  }

  getDetailsFromQueryParams(type: string, bookingId: string): void {
    if (type === 'BOOKING') {
      this.fleetBookingsLoading = true;
      this.fleetService
        .getAllBookingsByFleet(this.fleetId)
        .subscribe((res: any) => {
          this.fleetBookingsLoading = false;
          this.bookingOptions = res;
          this.setBookingDetails(bookingId);
        });
      this.accidentForm
        .get('bookingDetails')
        .get('booking')
        .addValidators(Validators.required);
      this.accidentForm
        .get('bookingDetails')
        .get('type')
        .addValidators(Validators.required);
      this.accidentForm
        .get('bookingDetails')
        .get('booking')
        .updateValueAndValidity({ emitEvent: false, onlySelf: true });
      this.accidentForm
        .get('bookingDetails')
        .get('type')
        .updateValueAndValidity({ emitEvent: false, onlySelf: true });
    } else {
      this.accidentForm.get('bookingDetails').get('booking').clearValidators();
      this.accidentForm.get('bookingDetails').get('type').clearValidators();
      this.accidentForm.get('bookingDetails').reset();
      this.accidentForm
        .get('bookingDetails')
        .get('booking')
        .updateValueAndValidity({ emitEvent: false, onlySelf: true });
      this.accidentForm
        .get('bookingDetails')
        .get('type')
        .updateValueAndValidity({ emitEvent: false, onlySelf: true });
      this.selectedBooking = null;
    }
  }

  goTo(type: string) {
    switch (type) {
      case 'booking':
        switch (this.selectedBooking.type) {
          case 'Rent':
            this.router.navigate([
              `admin/booking-rent/${this.selectedBooking._id}`,
            ]);
            break;
          case 'Transfer':
            this.router.navigate([
              `admin/booking-transfer/${this.selectedBooking._id}`,
            ]);
            break;
          case 'Other':
            this.router.navigate([
              `admin/booking-other/${this.selectedBooking._id}`,
            ]);
            break;
        }
        break;
      case 'customer':
        this.router.navigate([
          `admin/customers/${this.selectedBooking.customerId}`,
        ]);
        break;
    }
  }

  canExit(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.submit) {
      return true;
    } else if (this.accidentForm.dirty || this.accidentForm.touched) {
      return confirm(
        ' You have unsaved changes, Do you really want to discard these changes?',
      );
    } else {
      return true;
    }
  }

  takePicture() {
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        const video = document.createElement('video');
        video.srcObject = stream;

        // Wait for video to be ready to play
        video.addEventListener('canplay', () => {
          video
            .play()
            .then(() => {
              const canvas = document.createElement('canvas');
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;

              const context = canvas.getContext('2d');
              if (context) {
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                const imageData = canvas.toDataURL('image/jpeg');

                // Convert base64 data to binary
                const binaryData = atob(imageData.split(',')[1]);
                const arrayBuffer = new ArrayBuffer(binaryData.length);
                const uint8Array = new Uint8Array(arrayBuffer);
                for (let i = 0; i < binaryData.length; i++) {
                  uint8Array[i] = binaryData.charCodeAt(i);
                }
                const blob = new Blob([uint8Array], { type: 'image/jpeg' });

                // Pass the captured image data to the service method for uploading
                this.filesService.postFile(blob, 'TEST').subscribe(
                  (response) => {
                    console.log('Image uploaded successfully:', response);
                  },
                  (error) => {
                    console.error('Error uploading image:', error);
                  },
                );
              }

              // Pause the video after capturing the picture
              video.pause();
              stream.getVideoTracks()[0].stop();
            })
            .catch((error) => {
              console.error('Error playing video:', error);
            });
        });

        // Add video element to the DOM to trigger 'canplay' event
        document.body.appendChild(video);
      })
      .catch((error) => {
        console.error('Error accessing camera:', error);
      });
  }
}
